import { useState, useEffect } from 'react';
import { Chat, Message, ChatState } from '../types/chat';
import { v4 as uuidv4 } from 'uuid';
import { postData, sendChatMessage } from '../utils/api';
import logger from '../utils/logger';
import { marked } from 'marked';

// Это заглушка для имитации ответов от ИИ
// В реальности здесь должен быть запрос на сервер или API
const mockAiResponse = async (message: string): Promise<string> => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  return `Это ответ на ваше сообщение: "${message}"`;
};

export const useChat = () => {
  const [state, setState] = useState<ChatState>({
    chats: [],
    activeChat: null,
    isLoading: false,
    error: null
  });

  // Загрузка чатов из localStorage при инициализации
  useEffect(() => {
    try {
      const savedChats = localStorage.getItem('chats');
      if (savedChats) {
        const parsedChats = JSON.parse(savedChats) as Chat[];
        setState(prev => ({
          ...prev,
          chats: parsedChats,
          activeChat: parsedChats.length > 0 ? parsedChats[0] : null
        }));
      }
    } catch (error) {
      logger.error('Ошибка загрузки чатов:', error);
      setState(prev => ({ ...prev, error: 'Ошибка загрузки чатов' }));
    }
  }, []);

  // Сохранение чатов в localStorage при изменении
  useEffect(() => {
    if (state.chats.length > 0) {
      localStorage.setItem('chats', JSON.stringify(state.chats));
    }
  }, [state.chats]);

  // Создание нового чата через API
  const createChatOnServer = async (): Promise<string | null> => {
    try {
      const response = await postData<{ chatId: string }>('/ai/chat', {});
      if (response.success && response.data) {
        return response.data.chatId;
      }
      throw new Error(response.error || 'Не удалось создать чат');
    } catch (error) {
      logger.error('Ошибка при создании чата:', error);
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Ошибка при создании чата'
      }));
      return null;
    }
  };

  // Создание нового чата
  const createChat = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    const chatId = await createChatOnServer();
    if (!chatId) {
      setState(prev => ({ ...prev, isLoading: false }));
      return null;
    }

    const newChat: Chat = {
      id: chatId,
      title: `Новый чат ${state.chats.length + 1}`,
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    setState(prev => ({
      ...prev,
      chats: [newChat, ...prev.chats],
      activeChat: newChat,
      isLoading: false
    }));

    return newChat;
  };

  // Выбор активного чата
  const selectChat = (chatId: string) => {
    const selectedChat = state.chats.find(chat => chat.id === chatId);
    if (selectedChat) {
      setState(prev => ({ ...prev, activeChat: selectedChat }));
    }
  };

  // Отправка сообщения
  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    let currentChat = state.activeChat;
    if (!currentChat) {
      currentChat = await createChat();
      if (!currentChat) return;
    }

    // Создаем сообщение пользователя
    const userMessage: Message = {
      id: uuidv4(),
      content,
      isUserMessage: true,
      timestamp: Date.now()
    };

    // Обновляем состояние с сообщением пользователя
    const updatedChat: Chat = {
      ...currentChat,
      messages: [...currentChat.messages, userMessage],
      updatedAt: Date.now(),
      title: content.length > 30 ? `${content.substring(0, 30)}...` : content
    };

    setState(prev => ({
      ...prev,
      isLoading: true,
      chats: prev.chats.map(c => (c.id === updatedChat.id ? updatedChat : c)),
      activeChat: updatedChat
    }));

    try {
      // Создаем сообщение от ИИ
      const aiMessage: Message = {
        id: uuidv4(),
        content: '',
        isUserMessage: false,
        timestamp: Date.now()
      };

      // Добавляем сообщение ИИ в чат
      const chatWithAiMessage: Chat = {
        ...updatedChat,
        messages: [...updatedChat.messages, aiMessage],
        updatedAt: Date.now()
      };

      setState(prev => ({
        ...prev,
        chats: prev.chats.map(c => (c.id === chatWithAiMessage.id ? chatWithAiMessage : c)),
        activeChat: chatWithAiMessage
      }));

      // Собираем все чанки в одну строку
      let fullMessage = '';
      
      // Отправляем сообщение и получаем ответ
      await sendChatMessage(currentChat.id, content, (chunk) => {
        console.log(`chunk: '${chunk}'`);
        if (chunk.includes('\n')) {
          console.log('Найден перенос строки в чанке:', chunk);
        }
        // Заменяем пустой чанк на пробел через проверку длины
        const processedChunk = chunk.length === 0 ? '\n' : chunk;
        
        // Добавляем чанк к полному сообщению
        fullMessage += processedChunk;
        
        // Преобразуем полное сообщение в HTML синхронно
        const htmlContent = marked.parse(fullMessage) as string;
        
        setState(prev => {
          const currentChat = prev.activeChat;
          if (!currentChat) return prev;

          const updatedMessages = currentChat.messages.map(msg => 
            msg.id === aiMessage.id 
              ? { ...msg, content: htmlContent }
              : msg
          );

          const updatedChat: Chat = {
            ...currentChat,
            messages: updatedMessages,
            updatedAt: Date.now()
          };

          return {
            ...prev,
            chats: prev.chats.map(c => (c.id === updatedChat.id ? updatedChat : c)),
            activeChat: updatedChat
          };
        });
      });

      setState(prev => ({
        ...prev,
        isLoading: false
      }));
    } catch (error) {
      logger.error('Ошибка получения ответа:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Ошибка получения ответа от ИИ'
      }));
    }
  };

  // Удаление чата
  const deleteChat = (chatId: string) => {
    setState(prev => {
      const filteredChats = prev.chats.filter(chat => chat.id !== chatId);
      const newActiveChat = prev.activeChat?.id === chatId
        ? (filteredChats.length > 0 ? filteredChats[0] : null)
        : prev.activeChat;

      return {
        ...prev,
        chats: filteredChats,
        activeChat: newActiveChat
      };
    });
  };

  return {
    ...state,
    createChat,
    selectChat,
    sendMessage,
    deleteChat
  };
};

export default useChat; 